/**
 * Block Unit Factor computation for INSERT operations.
 *
 * AutoCAD's INSUNITS system variable controls the "drawing unit" (what
 * one length unit in the DXF represents). Every BLOCK_RECORD carries
 * its own INSUNITS (`insertionUnits`). When a block whose units differ
 * from the current drawing is inserted, AutoCAD auto-scales by
 * `drawingUnitsInMeters / blockUnitsInMeters` so the physical size of
 * the block geometry stays consistent.
 *
 *   Example: DWG is in millimeters (INSUNITS=4) and the BLOCK_RECORD
 *   has insertionUnits=1 (inches). AutoCAD applies scaleX *= 25.4 so a
 *   1-inch-wide block occupies 25.4 mm in the drawing.
 *
 * Unitless is a special case: if either value is 0 (Unitless) AutoCAD
 * performs no conversion (factor = 1).
 *
 * DXF reference — $INSUNITS header variable / group 70 on BLOCK_RECORD:
 *    0 = Unitless           1 = Inches            2 = Feet
 *    3 = Miles              4 = Millimeters       5 = Centimeters
 *    6 = Meters             7 = Kilometers        8 = Microinches
 *    9 = Mils              10 = Yards            11 = Angstroms
 *   12 = Nanometers        13 = Microns          14 = Decimeters
 *   15 = Decameters        16 = Hectometers      17 = Gigameters
 *   18 = Astronomical u.   19 = Light years      20 = Parsecs
 *   21 = US Survey Feet
 */

export const INSUNITS_TO_METERS: Record<number, number> = {
  0: 1, // Unitless — treated as factor 1 (no conversion)
  1: 0.0254, // Inches
  2: 0.3048, // Feet
  3: 1609.344, // Miles
  4: 0.001, // Millimeters
  5: 0.01, // Centimeters
  6: 1, // Meters
  7: 1000, // Kilometers
  8: 0.0000000254, // Microinches (2.54e-8)
  9: 0.0000254, // Mils (2.54e-5)
  10: 0.9144, // Yards
  11: 1e-10, // Angstroms
  12: 1e-9, // Nanometers
  13: 1e-6, // Microns
  14: 0.1, // Decimeters
  15: 10, // Decameters
  16: 100, // Hectometers
  17: 1e9, // Gigameters
  18: 1.495978707e11, // Astronomical units (AU)
  19: 9.4607304725808e15, // Light years
  // Parsec constant rounded to double precision — the full IAU value
  // 3.0856775814913673e16 loses precision at runtime.
  20: 3.0856775814913672e16,
  // US Survey Feet — distinct from International Feet (code 2 = 0.3048 m).
  // 1 US survey foot = 1200/3937 m ≈ 0.30480060960121924 m (the legacy NIST
  // definition; the ratio is exact, so keep it as a division rather than a
  // rounded decimal). Without this entry a survey-feet drawing falls back to
  // the unknown→1-meter default and scales inserted blocks wrongly.
  21: 1200 / 3937, // US Survey Feet
};

/**
 * Compute the unit-conversion multiplier AutoCAD applies when inserting
 * a block with `blockInsertionUnits` into a drawing with `drawingInsunits`.
 *
 * Returns a factor such that `block_scale *= factor` yields the correct
 * physical size. Both inputs are the INSUNITS integer (0-21).
 *
 * When either side is 0 (Unitless), returns 1 — AutoCAD's behaviour.
 */
export function computeBlockUnitFactor(
  drawingInsunits: number,
  blockInsertionUnits: number,
): number {
  if (drawingInsunits === 0 || blockInsertionUnits === 0) return 1;
  const blockInMeters = INSUNITS_TO_METERS[blockInsertionUnits] ?? 1;
  const drawingInMeters = INSUNITS_TO_METERS[drawingInsunits] ?? 1;
  if (drawingInMeters === 0) return 1;
  return blockInMeters / drawingInMeters;
}
